import React from 'react';
import PropTypes from 'prop-types';
import {
  BarChart3,
  ChevronDown,
  FileText,
  Image,
  Link2,
  Map,
  Plus,
  Quote,
  Table2,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../../ui/popover';

const INSERT_ICON_BY_ID = {
  table: Table2,
  link: Link2,
  image: Image,
  chart: BarChart3,
  map: Map,
  quote: Quote,
  placeholder: FileText,
};

const INSERT_DESCRIPTION_BY_ID = {
  chart: 'Insert an editable chart block for data visualization.',
  map: 'Insert an editable map block for spatial evidence.',
  placeholder: 'Insert a manual note placeholder for follow-up data.',
};

const preventMouseDownFocusLoss = (event) => {
  event.preventDefault();
};

export function ReportDraftInsertMenu({
  label,
  sections,
  wrapperZIndex,
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="report-draft-editor__toolbar-dropdown report-draft-editor__toolbar-dropdown--insert"
          aria-label={label}
          title={label}
          onMouseDown={preventMouseDownFocusLoss}
        >
          <Plus size={14} />
          <span className="report-draft-editor__toolbar-dropdown-label">{label}</span>
          <ChevronDown size={14} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={2}
        wrapperZIndex={wrapperZIndex}
        className="report-draft-editor__toolbar-menu report-draft-editor__toolbar-menu--insert"
      >
        {sections.map((section) => (
          <div key={section.id} className="report-draft-editor__toolbar-menu-section">
            {section.label ? (
              <div className="report-draft-editor__toolbar-menu-section-label">{section.label}</div>
            ) : null}
            {section.items.map((item) => {
              const RowIcon = INSERT_ICON_BY_ID[item.id] || Plus;
              const description = INSERT_DESCRIPTION_BY_ID[item.id];
              const hasDescription = Boolean(description);
              return (
                <button
                  key={item.id}
                  type="button"
                  className={`report-draft-editor__toolbar-menu-item${hasDescription ? ' report-draft-editor__toolbar-menu-item--with-description' : ''}`}
                  onMouseDown={preventMouseDownFocusLoss}
                  onClick={item.onSelect}
                >
                  <span className="report-draft-editor__toolbar-menu-item-leading">
                    <span className="report-draft-editor__toolbar-menu-item-icon" aria-hidden="true">
                      <RowIcon size={14} />
                    </span>
                    <span className="report-draft-editor__toolbar-menu-item-copy">
                      <span className="report-draft-editor__toolbar-menu-item-label">{item.label}</span>
                      {hasDescription ? (
                        <span className="report-draft-editor__toolbar-menu-item-description">
                          {description}
                        </span>
                      ) : null}
                    </span>
                  </span>
                  {item.shortcut ? (
                    <span className="report-draft-editor__toolbar-menu-shortcut report-draft-editor__toolbar-menu-shortcut--trailing">
                      {item.shortcut}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}

ReportDraftInsertMenu.propTypes = {
  label: PropTypes.string.isRequired,
  sections: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      shortcut: PropTypes.string,
      onSelect: PropTypes.func.isRequired,
    })).isRequired,
  })).isRequired,
  wrapperZIndex: PropTypes.number,
};

ReportDraftInsertMenu.defaultProps = {
  wrapperZIndex: undefined,
};
